import dayjs from 'dayjs'
import { Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { dateConversions, DatePickerInput } from '@dhl/inputs'
import { useGetOnboardingDataReportHook } from '@dhl/mdp-api'
import { Button, Card, Grid, H6 } from '@dhl-ui/components'

import { useNotificationMessage } from '~/common'
import { downloadCsv } from '~/common/util'
import { createDateOnlyValidationSchema } from '../model/validation'

type OnboardingReportData = {
  date: Date | null
}

export const OnboardingReportPage = () => {
  const { t } = useTranslation()
  const { showError } = useNotificationMessage()
  const getOnboardingDataReport = useGetOnboardingDataReportHook()
  const firstDayOfMonth = dayjs().startOf('month').toDate()
  const validationSchema = createDateOnlyValidationSchema(t)

  const onSubmit = async ({ date: dateStr }: OnboardingReportData) => {
    if (!dateStr) return
    const date = dayjs(dateStr).startOf('month')
    const year = date.year()
    const month = date.month() + 1 // month is 0-indexed in js
    try {
      const csvData = await getOnboardingDataReport({ month, year })
      downloadCsv(`onboarding-report-${month}-${year}.csv`, csvData)
    } catch (error) {
      console.error('Error downloading onboarding data report:', error)
      showError(t('admin.reportsPage.downloadOnboardingDataError'))
    }
  }

  return (
    <>
      <Formik
        initialValues={
          {
            date: firstDayOfMonth,
          } as OnboardingReportData
        }
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnChange={false}
        enableReinitialize
      >
        {({ isSubmitting, handleSubmit }) => (
          <Card>
            <Card.Header title={t('admin.reportsPage.onboardingData')} />
            <Card.Content>
              <Form data-testid="business-metrics-form">
                <Grid container spacing={2} direction="row" alignItems="center">
                  <Grid size={{ xs: 2 }}>
                    <H6>{t('admin.reportsPage.date')}</H6>
                  </Grid>
                  <Grid container size={{ xs: 10 }} spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <DatePickerInput
                        variant="standard"
                        label={t('admin.reportsPage.startDate')}
                        name="date"
                        conversions={dateConversions}
                        fullWidth
                        views={['month', 'year']}
                        format="MM-YYYY"
                      />
                    </Grid>
                  </Grid>
                  <Grid container size={12} sx={{ mt: 4, justifyContent: 'flex-end' }}>
                    <Button
                      size="medium"
                      disabled={isSubmitting}
                      onClick={() => {
                        handleSubmit()
                      }}
                      color="primary"
                      type="button"
                      variant="contained"
                    >
                      {t('admin.reportsPage.downloadOnboardingData')}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            </Card.Content>
          </Card>
        )}
      </Formik>
    </>
  )
}
